import React, { useState, useEffect } from 'react';
import { getFileCount } from './fileCollectionUtils';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../components/ui/collapsible';
import { Badge } from '../../components/ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../../components/ui/sheet';
import { ChevronDown, ChevronRight, Building2, FileText, Award, CreditCard, Image, Video, FolderOpen, Users, Menu } from 'lucide-react';

interface Organization {
  id: string;
  name: string;
  icon: React.ReactNode;
  categories: {
    id: string;
    name: string;
    icon: React.ReactNode;
    count: number;
  }[];
}

interface OrganizationMenuProps {
  onCategorySelect: (orgId: string, categoryId: string) => void;
  selectedCategory: { orgId: string; categoryId: string } | null;
  isMobile?: boolean;
  refetchCounts?: () => void;
}

const OrganizationMenu: React.FC<OrganizationMenuProps> = ({ onCategorySelect, selectedCategory, isMobile = false }) => {
  const [expandedOrgs, setExpandedOrgs] = useState<string[]>(['bytesplash']);

  const [categoryCounts, setCategoryCounts] = useState<Record<string, number>>({});
  const [loadingCounts, setLoadingCounts] = useState(true);

  const organizations: Organization[] = [
    {
      id: 'bytesplash',
      name: 'ByteSplash',
      icon: <Building2 className="h-4 w-4" />,
      categories: [
        { id: 'offerletters', name: 'Offer Letters', icon: <FileText className="h-4 w-4" />, count: Math.max(0, categoryCounts['bytesplash_offerletters'] ?? 0) },
        { id: 'courseCertificates', name: 'Course Certificates', icon: <Award className="h-4 w-4" />, count: Math.max(0, categoryCounts['bytesplash_courseCertificates'] ?? 0) },
        { id: 'iDCards', name: 'ID Cards', icon: <CreditCard className="h-4 w-4" />, count: Math.max(0, categoryCounts['bytesplash_iDCards'] ?? 0) },
        { id: 'Logos', name: 'Logo', icon: <Image className="h-4 w-4" />, count: Math.max(0, categoryCounts['bytesplash_Logos'] ?? 0) },
        { id: 'iDCardThreads', name: 'ID Card Threads', icon: <FolderOpen className="h-4 w-4" />, count: Math.max(0, categoryCounts['bytesplash_iDCardThreads'] ?? 0) },
        { id: 'drivequestionpapers', name: 'Drive Ques Papers', icon: <Award className="h-4 w-4" />, count: Math.max(0, categoryCounts['bytesplash_drivequestionpapers'] ?? 0) },
        { id: 'materials', name: 'Materials', icon: <FileText className="h-4 w-4" />, count: Math.max(0, categoryCounts['bytesplash_materials'] ?? 0) },
        { id: 'admin', name: 'Admin Docs', icon: <FileText className="h-4 w-4" />, count: Math.max(0, categoryCounts['bytesplash_admin'] ?? 0) },
        { id: 'recordedVideos', name: 'Recorded Videos', icon: <Video className="h-4 w-4" />, count: Math.max(0, categoryCounts['bytesplash_recordedVideos'] ?? 0) },
        { id: 'hrDocuments', name: 'HR Documents', icon: <FileText className="h-4 w-4" />, count: Math.max(0, categoryCounts['bytesplash_hrDocuments'] ?? 0) },
        { id: 'policyDocuments', name: 'Policy Documents', icon: <FileText className="h-4 w-4" />, count: Math.max(0, categoryCounts['bytesplash_policyDocuments'] ?? 0) },
        { id: 'jobOffers', name: 'Job Offers', icon: <FileText className="h-4 w-4" />, count: Math.max(0, categoryCounts['bytesplash_jobOffers'] ?? 0) },
        { id: 'jobBondsStudents', name: 'Job Bonds Students', icon: <FileText className="h-4 w-4" />, count: Math.max(0, categoryCounts['bytesplash_jobBondsStudents'] ?? 0) },
        { id: "employmentcertificates", name: "Employment Certificates", icon: <FileText className="h-4 w-4" />, count: Math.max(0, categoryCounts['bytesplash_employmentCertificates'] ?? 0) },
        { id: "payslips", name: "Payslips", icon: <FileText className="h-4 w-4" />, count: Math.max(0, categoryCounts['bytesplash_payslips'] ?? 0) },
        { id: "placementPosters", name: "Placement Posters", icon: <FileText className="h-4 w-4" />, count: Math.max(0, categoryCounts['bytesplash_placementPosters'] ?? 0) },
        { id: "pomplets", name: "Pomplets", icon: <FileText className="h-4 w-4" />, count: Math.max(0, categoryCounts['bytesplash_pomplets'] ?? 0) },
        { id:"allforms", name: "All Forms", icon: <FileText className="h-4 w-4" />, count: Math.max(0, categoryCounts['bytesplash_allforms'] ?? 0) },
        { id:"syllabusandtemplates", name: "Syllabus and Templates", icon: <FileText className="h-4 w-4" />, count: Math.max(0, categoryCounts['bytesplash_syllabusandtemplates'] ?? 0) },
        { id:"hrinvites", name: "HR Invites", icon: <Users className="h-4 w-4" />, count: Math.max(0, categoryCounts['bytesplash_hrinvites'] ?? 0) },
      { id:'crtinvites', name: 'CRT Invites', icon: <Users className="h-4 w-4" />, count: Math.max(0, categoryCounts['bytesplash_crtinvites'] ?? 0)   }
      ]
    },
    {
      id: 'margey-labs',
      name: 'Margey Labs',
      icon: <Building2 className="h-4 w-4" />,
      categories: [
        { id: 'offerletters', name: 'Offer Letters', icon: <FileText className="h-4 w-4" />, count: Math.max(0, categoryCounts['margeylabs_offerletters'] ?? 0) },
        { id: 'InternshipCertificates', name: 'Internship Certificates', icon: <Award className="h-4 w-4" />, count: Math.max(0, categoryCounts['margeylabs_InternshipCertificates'] ?? 0) },
        { id: 'IDCards', name: 'ID Cards', icon: <CreditCard className="h-4 w-4" />, count: Math.max(0, categoryCounts['margeylabs_IDCards'] ?? 0) },
        { id: 'Logos', name: 'Logo', icon: <Image className="h-4 w-4" />, count: Math.max(0, categoryCounts['margeylabs_Logos'] ?? 0) },
        { id: 'IDCardThreads', name: 'ID Card Threads', icon: <FolderOpen className="h-4 w-4" />, count: Math.max(0, categoryCounts['margeylabs_IDCardThreads'] ?? 0) },
        { id: 'materials', name: 'Materials', icon: <FileText className="h-4 w-4" />, count: Math.max(0, categoryCounts['margeylabs_materials'] ?? 0) },
        { id: 'recordedVideos', name: 'Recorded Videos', icon: <Video className="h-4 w-4" />, count: Math.max(0, categoryCounts['margeylabs_recordedVideos'] ?? 0) },
        { id: 'hrResources', name: 'HR Resources', icon: <Users className="h-4 w-4" />, count: Math.max(0, categoryCounts['margeylabs_hrResources'] ?? 0) }
      ]
    }
  ];

  const fetchCounts = async () => {
    setLoadingCounts(true);
    const counts: Record<string, number> = {};
    for (const org of organizations) {
      for (const cat of org.categories) {
        const count = await getFileCount(org.id, cat.id);
        counts[`${org.id}_${cat.id}`] = count;
      }
    }
    setCategoryCounts(counts);
    setLoadingCounts(false);
  };

  useEffect(() => {
    fetchCounts();
    // eslint-disable-next-line
  }, []);

  // Expose refetchCounts to parent via prop
  if (typeof window !== 'undefined') {
    (window as any).refetchOrgMenuCounts = fetchCounts;
  }

  const toggleOrganization = (orgId: string) => {
    setExpandedOrgs(prev => {
      const isExpanding = !prev.includes(orgId);
      if (isExpanding) {
        // Scroll the org header into view
        setTimeout(() => {
          const el = document.getElementById(`org-header-${orgId}`);
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 0);
        return [orgId];
      } else {
        return [];
      }
    });
  };

  const isSelected = (orgId: string, categoryId: string) => {
    return selectedCategory?.orgId === orgId && selectedCategory?.categoryId === categoryId;
  };

  const MenuContent = () => (
    <div className="space-y-4">
      {organizations.map((org) => (
        <Card key={org.id} className="shadow-sm">
          <Collapsible
            open={expandedOrgs.includes(org.id)}
            onOpenChange={() => toggleOrganization(org.id)}
          >
            <CollapsibleTrigger asChild>
              <CardHeader id={`org-header-${org.id}`} className="cursor-pointer hover:bg-muted/50 transition-colors pb-3">
                <CardTitle className="flex items-center justify-between text-base sm:text-lg">
                  <div className="flex items-center gap-2 sm:gap-3">
                    {org.icon}
                    <span className="text-sm sm:text-base">{org.name}</span>
                  </div>
                  {expandedOrgs.includes(org.id) ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </CardTitle>
              </CardHeader>
            </CollapsibleTrigger>

            <CollapsibleContent>
              <CardContent className="pt-0">
                <div className="grid gap-2">
                  {org.categories.map((category) => (
                    <Button
                      key={category.id}
                      variant={isSelected(org.id, category.id) ? "default" : "ghost"}
                      className="justify-between h-auto p-2 sm:p-3 text-left w-full max-w-full overflow-visible"
                      style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}
                      onClick={() => onCategorySelect(org.id, category.id)}
                    >
                      <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1 overflow-visible">
                        {category.icon}
                        <span className="font-medium text-xs sm:text-sm whitespace-normal break-words" style={{ wordBreak: 'break-word', minWidth: 0 }}>
                          {category.name}
                        </span>
                      </div>
                      <Badge variant="secondary" className="ml-2 text-xs flex-shrink-0" style={{ whiteSpace: 'nowrap' }}>
                        {loadingCounts ? '...' : category.count}
                      </Badge>
                    </Button>
                  ))}
                </div>
              </CardContent>
            </CollapsibleContent>
          </Collapsible>
        </Card>
      ))}
    </div>
  );

  if (isMobile) {
    return (
      <Sheet>
        <SheetTrigger asChild>
          <Button variant="outline" size="sm" className="gap-2">
            <Menu className="h-4 w-4" />
            Organizations
          </Button>
        </SheetTrigger>
        <SheetContent side="left" className="w-80">
          <SheetHeader>
            <SheetTitle>Organizations</SheetTitle>
          </SheetHeader>
          <div className="mt-6">
            <MenuContent />
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return <MenuContent />;
};

export default OrganizationMenu;